import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Card, CardActionArea, CardContent, CardHeader, Typography, Grid,
  Accordion, AccordionSummary, AccordionDetails, Button, MenuItem, Select, FormControl, InputLabel,
  Tooltip, Avatar
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import PaidIcon from '@mui/icons-material/Paid';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BadgeIcon from '@mui/icons-material/Badge';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import HttpsIcon from '@mui/icons-material/Https';
import PreviewIcon from '@mui/icons-material/Preview';

import { Space } from 'antd';
import { message, ConfigProvider, DatePicker, Input } from 'antd';

import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return value.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formattedNumber(value) {
  let number = parseNumber(value);
  return formatCurrency(number);
}

function ListCreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [allCreditNotes, setAllCreditNotes] = useState([]); // Store all credit notes for filtering
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // Nuevo estado para el filtro de estado de pago
  const [loading, setLoading] = useState(true);
  const [initializedFromURL, setInitializedFromURL] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const location = useLocation();

  const getCreditNotes = async () => {
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/credit-notes/list-credit-notes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();

        setCreditNotes(data);
        setAllCreditNotes(data); // Store all credit notes for filtering
        setLoading(false);
      } else if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar las notas de crédito, recargue la página.',
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error al cargar las notas de crédito, recargue la página.',
      });
      setLoading(false);
    }
  }

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Handle payment status change
  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      // Set time to beginning of first day and end of last day for proper range comparison
      const startDate = moment(dates[0]).startOf('day');
      const endDate = moment(dates[1]).endOf('day');
      setDateRange([startDate, endDate]);
    } else {
      setDateRange(null);
    }
  };

  // Apply filters to credit notes
  const applyFilters = useCallback(() => {
    let filteredCreditNotes = [...allCreditNotes];

    // Filter by payment status
    if (paymentStatus) {
      filteredCreditNotes = filteredCreditNotes.filter(
        creditNote => creditNote.paymentStatus.toLowerCase() === paymentStatus
      );
    }

    // Filter by search term (credit note number, client name)
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filteredCreditNotes = filteredCreditNotes.filter(
        creditNote =>
          creditNote.creditNoteNumber?.toString().toLowerCase().includes(searchTerm) ||
          creditNote.clientName?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by date range
    if (dateRange && dateRange.length === 2) {
      filteredCreditNotes = filteredCreditNotes.filter(creditNote => {
        if (!creditNote.creditNoteDate) return false;

        // Parse the credit note date string to a moment object
        const creditNoteDate = moment(creditNote.creditNoteDate);
        const startDate = dateRange[0];
        const endDate = dateRange[1];

        // For inclusive range, ensure we get complete days
        const isInRange = (
          creditNoteDate.isSameOrAfter(startDate, 'day') &&
          creditNoteDate.isSameOrBefore(endDate, 'day')
        );

        return isInRange;
      });
    }

    setCreditNotes(filteredCreditNotes);
  }, [allCreditNotes, search, paymentStatus, dateRange]);

  useEffect(() => {
    getCreditNotes();
  }, []);

  useEffect(() => {
    if (allCreditNotes.length > 0) {
      applyFilters();
    }
  }, [search, paymentStatus, dateRange, allCreditNotes, applyFilters]);

  useEffect(() => {
    if (initializedFromURL) return;
    const params = new URLSearchParams(location.search);
    let didInit = false;

    const q = params.get('search') || params.get('q');
    if (q) { setSearch(q); didInit = true; }

    const start = params.get('start') || params.get('from');
    const end = params.get('end') || params.get('to');
    if (start && end) {
      const mStart = moment(start, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      const mEnd = moment(end, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      if (mStart.isValid() && mEnd.isValid()) {
        const ordered = mStart.isAfter(mEnd) ? [mEnd, mStart] : [mStart, mEnd];
        setDateRange(ordered);
        didInit = true;
      }
    }

    const status = params.get('status');
    if (status && ['pendiente', 'pagado'].includes(status.toLowerCase())) {
      setPaymentStatus(status.toLowerCase());
      didInit = true;
    }

    setInitializedFromURL(true);
    if (didInit) {
      // applyFilters se disparará por efecto existente
    }
  }, [location.search, initializedFromURL]);

  useEffect(() => {
    if (!initializedFromURL) return;
    const params = new URLSearchParams(location.search);

    if (search) params.set('search', search); else params.delete('search');

    if (dateRange && dateRange.length === 2) {
      params.set('start', dateRange[0].format('YYYY-MM-DD'));
      params.set('end', dateRange[1].format('YYYY-MM-DD'));
    } else {
      params.delete('start');
      params.delete('end');
    }

    if (paymentStatus) params.set('status', paymentStatus); else params.delete('status');

    const newSearch = params.toString();
    const target = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
  }, [search, dateRange, paymentStatus, initializedFromURL]);

  if (loading) {
    return (
      <Container sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '90vh',
        background: '#e3f2fd'
      }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            borderRadius: '16px',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px 0 rgba(25,118,210,0.18)',
            border: '1.5px solid #1976d2',
            maxWidth: '400px',
            width: '100%'
          }}
        >
          <Box sx={{ position: 'relative', mb: 3 }}>
            <ReceiptLongIcon
              sx={{
                color: '#1976d2',
                fontSize: 60,
                animation: 'pulse 1.5s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%': { opacity: 0.6, transform: 'scale(0.95)' },
                  '50%': { opacity: 1, transform: 'scale(1.05)' },
                  '100%': { opacity: 0.6, transform: 'scale(0.95)' }
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'spin 2s infinite linear',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              <Box
                sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  border: '3px solid transparent',
                  borderTop: '3px solid #0d47a1',
                  borderRight: '3px solid #0d47a1'
                }}
              />
            </Box>
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: '#0d47a1',
              fontWeight: 700,
              mb: 1,
              textAlign: 'center'
            }}
          >
            Cargando notas de crédito
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#1976d2',
                  borderRadius: '50%',
                  mx: 0.5,
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: `${dot * 0.16}s`,
                  '@keyframes bounce': {
                    '0%, 80%, 100%': { transform: 'scale(0)' },
                    '40%': { transform: 'scale(1)' }
                  }
                }}
              />
            ))}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: '#42a5f5',
              mt: 2,
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Estamos solicitando la información de tus notas de crédito
          </Typography>
        </Box>
      </Container>
    );
  };

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div style={{ background: '#e3f2fd', minHeight: '90vh', overflow: 'auto' }}>
      <Container
        sx={{
          padding: '32px 0',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
        }}
        maxWidth="lg"
      >
        {contextHolder}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%' }}>
          <Typography
            variant="h4"
            sx={{
              marginBottom: '28px',
              color: '#0d47a1',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textAlign: 'center',
            }}
          >
            Registro de Notas de Crédito
          </Typography>

          {/* Filter Section */}
          <Card sx={{ width: '100%', mb: 4, borderRadius: '18px', border: '1.5px solid #1976d2' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#0d47a1',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SearchIcon sx={{ mr: 1, color: '#1976d2' }} />
                Filtrar notas de crédito
              </Typography>
              <Grid container spacing={2}>
                {/* Search Input */}
                <Grid item xs={12} md={4}>
                  <ConfigProvider theme={{ token: { colorPrimary: '#1976d2' } }}>
                    <Input
                      placeholder="Buscar por número o cliente"
                      value={search}
                      onChange={handleSearchChange}
                      allowClear
                      size="large"
                    />
                  </ConfigProvider>
                </Grid>

                {/* Date Range Picker */}
                <Grid item xs={12} md={5}>
                  <ConfigProvider locale={esES} theme={{ token: { colorPrimary: '#1976d2' } }}>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        onChange={handleDateRangeChange}
                        format="DD/MM/YYYY"
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        allowClear={true}
                        size="large"
                        ranges={{
                          'Este Mes': [moment().startOf('month'), moment().endOf('month')],
                          'Mes Anterior': [moment().subtract(1, 'months').startOf('month'), moment().subtract(1, 'months').endOf('month')],
                          'Últimos 30 Días': [moment().subtract(29, 'days'), moment()]
                        }}
                      />
                    </Space>
                  </ConfigProvider>
                </Grid>

                {/* Payment Status Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="payment-status-select-label"
                      sx={{ color: '#0d47a1' }}>
                      Estado de pago
                    </InputLabel>
                    <Select
                      labelId="payment-status-select-label"
                      id="payment-status-select-label"
                      value={paymentStatus}
                      onChange={handlePaymentStatusChange}
                      label="Estado de pago"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="pagado">Pagado</MenuItem>
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Existing credit notes list rendering */}
          {creditNotes.length > 0 ? creditNotes.map((creditNote, index) => (
            <Grid container spacing={1} key={index} sx={{ marginBottom: '24px', justifyContent: 'center' }}>
              <Grid item xs={12} md={10}>
                <Card
                  sx={{
                    width: "100%",
                    borderRadius: '18px',
                    border: '1.5px solid #1976d2',
                    boxShadow: '0 4px 16px 0 rgba(25,118,210,0.10)',
                    background: 'linear-gradient(90deg, #fff 80%, #e3f2fd 100%)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 32px 0 rgba(25,118,210,0.18)',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  {/* Header */}
                  <CardHeader
                    title={
                      <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
                        <Grid item sm={6} xs={10} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1 }}>
                            <ReceiptLongIcon sx={{ color: '#1976d2', fontSize: 32, mr: 1 }} />
                            <Box>
                              <Typography
                                sx={{
                                  color: '#0d47a1',
                                  fontWeight: 700,
                                  fontSize: { xs: '1rem', sm: '1.15rem' },
                                  m: 0,
                                  lineHeight: { xs: 1.05, sm: 1.2 }
                                }}
                                component="div"
                              >
                                Nota de crédito para {creditNote.clientName}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#42a5f5',
                                  fontWeight: 500,
                                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                  mt: 0.5,
                                  lineHeight: { xs: 1.05, ms: 1.2 }
                                }}
                                component="div"
                              >
                                Número de nota: <span style={{ fontWeight: 700 }}>{creditNote.creditNoteNumber}</span>
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item sm={6} xs={2} sx={{ textAlign: 'right' }}>
                          <Typography
                            sx={{
                              color: '#42a5f5',
                              fontWeight: 700,
                              fontSize: { xs: '0.95rem', sm: '1.15rem' },
                              m: 0,
                              lineHeight: { xs: 1, sm: 1.2 }
                            }}
                            component="div"
                          >
                            <IconButton onClick={() => navigate(`/billing/credit-note/${creditNote.creditNoteID}`)}>
                              <Tooltip title="Gestionar nota de crédito" arrow>
                                <PreviewIcon sx={{ color: '#42a5f5', fontSize: 26 }} />
                              </Tooltip>
                            </IconButton>
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                    sx={{ pb: 0, pt: 2, px: 3, background: '#f1f8e9', borderTopLeftRadius: 18, borderTopRightRadius: 18 }}
                  />
                  {/* Rest of the card content */}
                  <CardContent sx={{ pt: 2, pb: 2, px: { xs: 1, sm: 3 } }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <PersonIcon sx={{ color: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Cliente: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{creditNote.clientName}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ReceiptIcon sx={{ color: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Factura relacionada: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{creditNote.invoiceNumber}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <BadgeIcon sx={{ color: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Atendido por: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{creditNote.employeeName}</span>
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ReceiptIcon sx={{ color: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Monto: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{formatCurrency(parseNumber(creditNote.amount))}</span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <EventNoteIcon sx={{ color: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Fecha de emisión: <span style={{ color: '#0d47a1', fontWeight: 700 }}>
                              {creditNote.creditNoteDate
                                ? new Date(creditNote.creditNoteDate).toLocaleString('es-DO', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })
                                : 'No registrada'}
                            </span>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          {
                            creditNote.paymentStatus === "pagado" ? (
                              <PaidIcon sx={{ color: '#22c55e', mr: 1 }} />
                            ) : (
                              <RequestQuoteIcon sx={{ color: '#d97706', mr: 1 }} />
                            )
                          }
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            Estado de pago: <span style={{ color: creditNote.paymentStatus === "pagado" ? "green" : "#d97706", fontWeight: 700 }}>{creditNote.paymentStatus.toUpperCase()}</span>
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ width: '100%' }}>
                      <Accordion sx={{ borderRadius: '8px', width: '100%' }}>
                        <CardActionArea
                          sx={{
                            mt: 3,
                            background: '#1976d2',
                            color: '#fff',
                            transition: 'background 0.2s',
                            '&:hover': { background: '#1565c0' },
                            boxShadow: '0 2px 8px 0 rgba(25,118,210,0.10)'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ArrowDropDownIcon sx={{ fontWeight: 700, margin: 0, padding: 0, color: '#fff' }} />}
                            aria-controls="panel2-content"
                            id="panel2-header"
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0, m: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, margin: 0, padding: 0 }}>
                                Ver detalles de la nota de crédito
                              </Typography>
                            </Box>
                          </AccordionSummary>
                        </CardActionArea>
                        <AccordionDetails sx={{ padding: 0, margin: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', py: 1, flexDirection: 'column', width: '100%' }}>
                            <CreditNoteDetails creditNote={creditNote} index={index} />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )) : (
            <Typography variant="h6" sx={{ color: '#0d47a1', mt: 4 }}>
              No hay notas de crédito que coincidan con los filtros aplicados.
            </Typography>
          )}
        </Box>
      </Container>
    </div>
  )
};

function CreditNoteDetails({ creditNote, index }) {
  return (
    <Container sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Typography
          variant="h5"
          sx={{
            marginBottom: '28px',
            color: '#0d47a1',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textAlign: 'center',
            marginTop: '28px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <HistoryIcon sx={{ color: '#0d47a1', mr: 1 }} />
          Detalles de la nota de crédito
        </Typography>

        <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', borderRadius: 2, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Número de nota: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{creditNote.creditNoteNumber}</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Monto: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{formattedNumber(creditNote.amount)}</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Razón: <span style={{ color: '#0d47a1', fontWeight: 700 }}>{creditNote.reason}</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Estado de pago: <span style={{ color: creditNote.paymentStatus === "pagado" ? "green" : "#d97706", fontWeight: 700 }}>{creditNote.paymentStatus.toUpperCase()}</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Fecha de creación: <span style={{ color: '#0d47a1', fontWeight: 700 }}>
                  {creditNote.creditNoteDate
                    ? new Date(creditNote.creditNoteDate).toLocaleString('es-DO')
                    : 'No disponible'}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                Última actualización: <span style={{ color: '#0d47a1', fontWeight: 700 }}>
                  {creditNote.updatedAt
                    ? new Date(creditNote.updatedAt).toLocaleString('es-DO')
                    : 'No disponible'}
                </span>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default ListCreditNotes
